'use client'

import { useState } from 'react'

import { LinkField, linkUrl } from '@/lib/link'

import styles from './Footer.module.css'

export interface ExtendedLinkField {
  _uid?: string
  component?: string
  _editable?: string
  isPopularSearches?: boolean
  title: string
  link?: LinkField
  items?: ExtendedLinkField[]
}
export type FooterProps = {
  desktopMenu?: {
    title: string
    link?: LinkField
    isPopularSearches?: boolean
    items: ExtendedLinkField[]
  }[]
  mobileMenu?: ExtendedLinkField[]
  socialMenu?: {
    icon: string
    link: LinkField
  }[]
  copy: string
  disclaimer: React.ReactNode
}

type PopularSearchLink = { title: string; href?: string }

export const PhnxFooter: React.FC<FooterProps> = ({
  desktopMenu,
  mobileMenu,
  socialMenu,
  copy,
  disclaimer,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const [isPopularSearchesOpen, setIsPopularSearchesOpen] = useState(false)

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedItems(newExpanded)
  }

  const isPopularSearchesItem = (item: ExtendedLinkField) => item.isPopularSearches === true

  const normalizeTitle = (title: string) => title.trim().toLowerCase()
  const isResourcesTitle = (title: string) => normalizeTitle(title) === 'resources'

  const findPopularSearchesNode = (
    nodes: ExtendedLinkField[] | undefined
  ): ExtendedLinkField | undefined => {
    if (!nodes || nodes.length === 0) return undefined

    for (const node of nodes) {
      if (isPopularSearchesItem(node) && (node.items?.length ?? 0) > 0) return node
      const nested = findPopularSearchesNode(node.items)
      if (nested) return nested
    }

    return undefined
  }

  const extractPopularSearchLinks = (node?: ExtendedLinkField): PopularSearchLink[] => {
    if (!node) return []
    const out: PopularSearchLink[] = []
    const walk = (n: ExtendedLinkField) => {
      if (!n.title) return
      const rawHref = n.link ? linkUrl(n.link) : undefined
      // '/' means an empty story link — treat as no URL
      const href = rawHref && rawHref !== '/' ? rawHref : undefined
      out.push({ title: n.title, href })
      if (n.items && n.items.length > 0) n.items.forEach(walk)
    }
    if (node.items) node.items.forEach(walk)
    return out
  }
  // Popular Searches can appear either as a full top-level column (desktopMenu group),
  // a top-level item (mobileMenu), or nested somewhere in the tree depending on CMS setup.
  const popularSearchesDesktopGroup = desktopMenu?.find((g) => g.isPopularSearches === true)
  const popularSearchesMobileTop = mobileMenu?.find((n) => n.isPopularSearches === true)

  const popularSearchesFromDesktopGroup = popularSearchesDesktopGroup
    ? ({
        title: popularSearchesDesktopGroup.title,
        items: popularSearchesDesktopGroup.items,
        isPopularSearches: true,
      } as ExtendedLinkField)
    : undefined

  const popularSearchesFromDesktopNested = findPopularSearchesNode(
    desktopMenu?.flatMap((g) => g.items)
  )
  const popularSearchesFromMobileNested = findPopularSearchesNode(mobileMenu)

  const popularSearchLinks =
    extractPopularSearchLinks(popularSearchesFromDesktopGroup).length > 0
      ? extractPopularSearchLinks(popularSearchesFromDesktopGroup)
      : extractPopularSearchLinks(popularSearchesMobileTop).length > 0
        ? extractPopularSearchLinks(popularSearchesMobileTop)
        : extractPopularSearchLinks(popularSearchesFromDesktopNested).length > 0
          ? extractPopularSearchLinks(popularSearchesFromDesktopNested)
          : extractPopularSearchLinks(popularSearchesFromMobileNested)

  const popularPanelId = 'footer-popular-searches-panel'

  // The trigger renders whenever the isPopularSearches node exists in the CMS data.
  // The link panel only opens if there are valid child links.
  const hasPopularSearchesNode =
    popularSearchesDesktopGroup != null ||
    popularSearchesMobileTop != null ||
    popularSearchesFromDesktopNested != null ||
    popularSearchesFromMobileNested != null

  const popularSearchesLabel =
    popularSearchesDesktopGroup?.title ??
    popularSearchesMobileTop?.title ??
    popularSearchesFromDesktopNested?.title ??
    popularSearchesFromMobileNested?.title ??
    'Popular Searches'

  const renderPopularSearchesTrigger = (className: string) => (
    <button
      type="button"
      className={className}
      onClick={() => setIsPopularSearchesOpen((v) => !v)}
      aria-expanded={isPopularSearchesOpen}
      aria-controls={popularPanelId}
    >
      {popularSearchesLabel}
    </button>
  )

  return (
    <div className={styles['footer']}>
      <footer>
        <div className="container">
          <div className={`${styles.menu} ${styles.desktop}`}>
            {desktopMenu && (
              <ul>
                {desktopMenu
                  // If Popular Searches is a top-level CMS column, hide it here (wrong position in UI).
                  .filter((item) => item.isPopularSearches !== true)
                  .map((item, index) => (
                    <li key={item.title ?? index}>
                      <span>{item.title}</span>
                      <ul>
                        {item.items.map((subItem, index) => (
                          <li key={subItem._uid ?? index}>
                            {isPopularSearchesItem(subItem) && hasPopularSearchesNode ? (
                              renderPopularSearchesTrigger(styles.popularSearchesHeaderTrigger)
                            ) : (
                              <>
                                {subItem.link &&
                                subItem.link.cached_url &&
                                subItem.link.cached_url !== '/en-nz/' &&
                                !(subItem.items && subItem.items.length > 0) ? (
                                  <a href={linkUrl(subItem.link)}>{subItem.title}</a>
                                ) : (
                                  <span
                                    className={
                                      subItem.items && subItem.items.length > 0
                                        ? styles.desktopCategoryHeading
                                        : styles.menuItemText
                                    }
                                  >
                                    {subItem.title}
                                  </span>
                                )}

                                {subItem.items && subItem.items.length > 0 && (
                                  <ul>
                                    {subItem.items.map((subSubItem, index) => (
                                      <li key={subSubItem._uid ?? index}>
                                        {subSubItem.link && subSubItem.link.cached_url && (
                                          <a href={linkUrl(subSubItem.link)}>{subSubItem.title}</a>
                                        )}
                                      </li>
                                    ))}
                                    {isResourcesTitle(subItem.title) && hasPopularSearchesNode && (
                                      <li key="popular-searches-injected">
                                        {renderPopularSearchesTrigger(
                                          styles.popularSearchesHeaderTrigger
                                        )}
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {mobileMenu && (
            <div className={`${styles.menu} ${styles.mobile}`}>
              <ul>
                {mobileMenu.map((item, index) => (
                  <li key={item._uid ?? index}>
                    <div className={styles.mobileMenuItem}>
                      {item.isPopularSearches === true && hasPopularSearchesNode ? (
                        renderPopularSearchesTrigger(
                          `${styles.mobileMenuLink} ${styles.popularSearchesTrigger}`
                        )
                      ) : item.items && item.items.length > 0 ? (
                        <span className={styles.mobileMenuLink}>{item.title}</span>
                      ) : item.link &&
                        item.link.cached_url &&
                        item.link.cached_url !== '/en-nz/' ? (
                        <a href={linkUrl(item.link)} className={styles.mobileMenuLink}>
                          {item.title}
                        </a>
                      ) : (
                        <span className={styles.mobileMenuLink}>{item.title}</span>
                      )}

                      {item.items && item.items.length > 0 && item.isPopularSearches !== true && (
                        <button
                          onClick={() => toggleExpanded(index)}
                          className={styles.expandButton}
                          aria-expanded={expandedItems.has(index)}
                          aria-label={`${expandedItems.has(index) ? 'Collapse' : 'Expand'} ${item.title} menu`}
                        ></button>
                      )}
                    </div>

                    {item.items && item.items.length > 0 && item.isPopularSearches !== true && (
                      <ul
                        className={`${styles.subMenu} ${expandedItems.has(index) ? styles.expanded : styles.collapsed}`}
                        aria-hidden={!expandedItems.has(index)}
                      >
                        {item.items.map((subItem, subIndex) => (
                          <li key={subItem._uid ?? subIndex}>
                            {isPopularSearchesItem(subItem) && hasPopularSearchesNode ? (
                              renderPopularSearchesTrigger(
                                `${styles.subMenuLink} ${styles.popularSearchesTrigger}`
                              )
                            ) : (
                              <>
                                {subItem.items && subItem.items.length > 0 ? (
                                  <>
                                    <span className={styles.categoryHeading}>{subItem.title}</span>
                                    <ul className={styles.nestedSubMenu}>
                                      {subItem.items.map((nestedItem, nestedIndex) => (
                                        <li key={nestedItem._uid ?? nestedIndex}>
                                          {nestedItem.link && nestedItem.link.cached_url && (
                                            <a
                                              href={linkUrl(nestedItem.link)}
                                              className={styles.nestedSubMenuLink}
                                            >
                                              {nestedItem.title}
                                            </a>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                ) : subItem.link && subItem.link.cached_url ? (
                                  <a href={linkUrl(subItem.link)} className={styles.subMenuLink}>
                                    {subItem.title}
                                  </a>
                                ) : (
                                  <span className={styles.categoryHeading}>{subItem.title}</span>
                                )}
                              </>
                            )}
                          </li>
                        ))}
                        {isResourcesTitle(item.title) && hasPopularSearchesNode && (
                          <li key="popular-searches-injected-mobile">
                            {renderPopularSearchesTrigger(
                              `${styles.subMenuLink} ${styles.popularSearchesTrigger}`
                            )}
                          </li>
                        )}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.bottom}>
          <div className={styles.social}>
            {socialMenu && (
              <ul>
                {socialMenu.map((item, index) => (
                  <li key={index}>
                    <a href={linkUrl(item.link)}>
                      <span className={`fx-icon ${styles['icon']} ${styles['right']} `}>
                        <span className={`fx-icon ${item.icon}`}></span>
                        <span className="sr-hidden">{item.link.cached_url} link</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className={styles.backtotop}>
            <a href="#top">
              <div className={styles.icon}>
                <span className={`fx-icon`}>
                  <span className={`fx-icon fx-icon-arrow-up-lg`}></span>
                </span>
              </div>
              <div className={styles.text}>Back to top</div>
            </a>
          </div>
        </div>

        {isPopularSearchesOpen && hasPopularSearchesNode && (
          <div className={styles.popularSearchesArea} id={popularPanelId}>
            <div className="container">
              <div
                className={styles.popularSearchesPanel}
                role="region"
                aria-label="Popular Searches"
              >
                <div className={styles.popularSearchesPanelHeader}>
                  <span className={styles.popularSearchesPanelTitle}>{popularSearchesLabel}</span>
                  <button
                    type="button"
                    className={styles.popularSearchesPanelClose}
                    onClick={() => setIsPopularSearchesOpen(false)}
                    aria-label="Close Popular Searches panel"
                  >
                    ×
                  </button>
                </div>
                {popularSearchLinks.length > 0 && (
                  <ul className={styles.popularSearchesGrid}>
                    {popularSearchLinks.map((linkItem, i) => (
                      <li key={linkItem.href ?? linkItem.title ?? i}>
                        {linkItem.href ? (
                          <a href={linkItem.href}>{linkItem.title}</a>
                        ) : (
                          <span>{linkItem.title}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.disclaimer}>
          {copy != null && copy !== '' && <p>{copy}</p>}
          {disclaimer != null && disclaimer}
        </div>
      </footer>
    </div>
  )
}
